import styled from "styled-components";
import { useQuery } from "@tanstack/react-query";
import JSONPretty from "react-json-pretty";
import { withAppHOC } from "../../hoc";
import { StyledContentContainer } from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import { TableBuilder } from "../../components/TableElements";
import { API_CONFIG } from "../../constants";
import API from "../../api";
import { isArray } from "lodash";
import { getAuthData } from "../../utils";

// Styled Components
const StyledContentInnerContainer = styled.section`
  padding: 20px;
`;

const SectionContainer = styled.section`
  font-size: 14px;
  color: #000;
  line-height: 1.6;
  margin-bottom: 25px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  background: #fafafa;
`;

const SectionHeader = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  background-color: #f0f0f0;
  padding: 12px 20px;
  margin: 0;
  border-bottom: 1px solid #ddd;
`;

const SectionContent = styled.div`
  padding: 15px 20px;
  word-break: break-word;
  overflow-x: auto;

  p {
    margin-bottom: 10px;
  }

  code {
    background-color: #eee;
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 13px;
  }
`;

const ApiTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: #222;
  margin-bottom: 20px;
`;

const APIDocumentation = () => {
  const {
    userProfile: { isGSTSeller },
  } = getAuthData();
  const { API_BASE_URL } = API_CONFIG;

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta?operatorSortById=Yes`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data } = useQuery({
    queryKey: ["meta"],
    queryFn: readMeta,
  });

  const apiSections = [
    {
      title: "Recharge API",
      request: {
        method: "GET",
        url: `${API_BASE_URL}/public/recharge?apiid=[apiid]&apitoken=[apitoken]&number=[number]&operator=[operator]&amount=[amount]&refnumber=[refnumber]`,
        fields: [
          { field: "apiid", description: "Api id provided by us", fieldType: "Mandatory" },
          { field: "apitoken", description: "Api token provided by us", fieldType: "Mandatory" },
          { field: "number", description: "Recharge mobile/account number", fieldType: "Mandatory" },
          { field: "operator", description: "Our operators id", fieldType: "Mandatory" },
          { field: "amount", description: "Recharge amount", fieldType: "Mandatory" },
          { field: "refnumber", description: "Your transactions id", fieldType: "Mandatory" },
        ],
      },
      response: {
        type: "JSON",
        example: {
          status: "Success/Failed/Pending",
          message: "Transaction Information",
          transactionsid: "Our transactions id",
          number: "Recharge number",
          amount: "Recharge amount",
          margin: "Your margin",
          operator: "Operator name",
          operatorsid: "Recharge operators id",
          refnumber: "Your transaction id",
        },
      },
    },
    {
      title: "Status Check API",
      request: {
        method: "GET",
        url: `${API_BASE_URL}/public/status?apiid=[apiid]&apitoken=[apitoken]&transactionsid=[transactionsid]&refnumber=[refnumber]`,
        fields: [
          { field: "apiid", description: "Api id provided by us", fieldType: "Mandatory" },
          { field: "apitoken", description: "Api token provided by us", fieldType: "Mandatory" },
          { field: "transactionsid", description: "Our transactions id", fieldType: "Mandatory" },
          { field: "refnumber", description: "Your transactions id", fieldType: "Optional" },
        ],
      },
      response: {
        type: "JSON",
        example: {
          status: "Success/Failed/Pending",
          message: "Transaction Information",
          transactionsid: "Our transactions id",
          number: "Recharge number",
          amount: "Recharge amount",
          margin: "Your margin",
          operator: "Operator name",
          operatorsid: "Recharge operators id",
          refnumber: "Your transaction id",
        },
      },
    },
    {
      title: "Balance Check API",
      request: {
        method: "GET",
        url: `${API_BASE_URL}/public/balance?apiid=[apiid]&apitoken=[apitoken]`,
        fields: [
          { field: "apiid", description: "Api id provided by us", fieldType: "Mandatory" },
          { field: "apitoken", description: "Api token provided by us", fieldType: "Mandatory" },
        ],
      },
      response: {
        type: "JSON",
        example: {
          status: "Success/Failed",
          message: "Your available balance",
          buyerWallet: "Buyer wallet balance",
          sellerWallet: "Seller wallet balance",
        },
      },
    },
    {
      title: "Complaint API",
      request: {
        method: "GET",
        url: `${API_BASE_URL}/public/complaint?apiid=[apiid]&apitoken=[apitoken]&transactionsid=[transactionsid]`,
        fields: [
          { field: "apiid", description: "Api id provided by us", fieldType: "Mandatory" },
          { field: "apitoken", description: "Api token provided by us", fieldType: "Mandatory" },
          { field: "transactionsid", description: "Our transactions id", fieldType: "Mandatory" },
        ],
      },
      response: {
        type: "JSON",
        example: {
          status: "Success/Failed",
          message: "Complaint created successfully",
        },
      },
    },
  ];

  return (
    <>
      <PageHeader title="API Documentation" />
      <StyledContentContainer>
        <StyledContentInnerContainer>
          {apiSections.map((section, idx) => (
            <SectionContainer key={idx}>
              <ApiTitle>{section.title}</ApiTitle>
              <SectionHeader>API Request</SectionHeader>
              <SectionContent>
                <p>
                  <b>Method:</b> {section.request.method}
                </p>
                <p>
                  <b>URL:</b> <code>{section.request.url}</code>
                </p>
                <TableBuilder
                  tableHeadings={[
                    { title: "Field", dataSelector: "field", dataType: "string", canSort: false },
                    { title: "Description", dataSelector: "description", dataType: "string", canSort: false },
                    { title: "Mandatory/Optional", dataSelector: "fieldType", dataType: "string", canSort: false },
                    { title: "Remark", dataSelector: "remark", dataType: "string", canSort: false },
                  ]}
                  tableData={section.request.fields.map((f) => ({ ...f, remark: "" }))}
                />
              </SectionContent>

              <SectionHeader>Response</SectionHeader>
              <SectionContent>
                <p>
                  <b>Response Type:</b> {section.response.type}
                </p>
                <JSONPretty id="json-pretty" data={JSON.stringify(section.response.example)} />
              </SectionContent>
            </SectionContainer>
          ))}

          {data?.operators && isArray(data?.operators) && data.operators.length > 0 && (
            <SectionContainer>
              <ApiTitle>Operators List</ApiTitle>
              <TableBuilder
                tableHeadings={[
                  { title: "Operator Name", dataSelector: "operatorName", dataType: "string" },
                  { title: "Operator ID", dataSelector: "id", dataType: "number" },
                ]}
                tableData={data.operators}
              />
            </SectionContainer>
          )}
        </StyledContentInnerContainer>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(APIDocumentation);
